import { Download } from "lucide-react";

const MARKS = [
  {
    id: "crest",
    file: "/logo/crest.png",
    label: "The Crest",
    use: "Helmets · Jersey chest · Hoodie back",
    note: "Primary mark. Vintage screenprint feel.",
    pad: "p-6 md:p-10",
  },
  {
    id: "wordmark",
    file: "/logo/wordmark.png",
    label: "The Wordmark",
    use: "Tee fronts · Banners · Posters",
    note: "Stacked. Hand-cut letterforms. Loud.",
    pad: "p-10 md:p-16",
  },
  {
    id: "monogram",
    file: "/logo/monogram.png",
    label: "The Monogram",
    use: "Hats · Sleeves · Socks · Stick tape",
    note: "Compact mark for small surfaces.",
    pad: "p-10 md:p-16",
  },
];

export default function Marks() {
  return (
    <section
      id="marks"
      data-testid="marks-section"
      className="relative bg-[#0a0a0a] py-24 md:py-32 border-t border-neutral-900"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-14 md:mb-20 items-end">
          <div className="md:col-span-7">
            <div className="flex items-center gap-3 text-[10px] tracking-[0.3em] uppercase text-[#e11d2a] mb-5">
              <span className="inline-block w-8 h-px bg-[#e11d2a]" />
              <span>The Identity · Brand System</span>
            </div>
            <h2 className="font-anton uppercase text-5xl sm:text-6xl md:text-7xl leading-[0.9] tracking-tight">
              Three marks. <br />
              <span className="text-[#e11d2a]">One squad.</span>
            </h2>
          </div>
          <div className="md:col-span-4 md:col-start-9">
            <p className="text-neutral-400 text-sm md:text-base leading-relaxed">
              A full team system, built to print clean on anything from a fitted
              cap to a stadium banner. Pull whichever mark fits the canvas.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
          {/* Crest — featured */}
          <MarkCard
            mark={MARKS[0]}
            featured
            className="md:col-span-6 md:row-span-2 min-h-[420px] md:min-h-[640px]"
          />
          <MarkCard mark={MARKS[1]} className="md:col-span-6 min-h-[300px]" />
          <MarkCard mark={MARKS[2]} className="md:col-span-6 min-h-[300px]" />
        </div>

        {/* Apparel mockup band */}
        <div className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
          <ApparelMock
            title="Game Hoodie"
            tag="Crest · Center Chest"
            file="/logo/crest.png"
            bg="#0a0a0a"
            ink="white"
            className="md:col-span-4"
          />
          <ApparelMock
            title="Practice Tee"
            tag="Wordmark · Front"
            file="/logo/wordmark.png"
            bg="#e11d2a"
            ink="white"
            className="md:col-span-4"
          />
          <ApparelMock
            title="Trucker Cap"
            tag="Monogram · Front Panel"
            file="/logo/monogram.png"
            bg="#1b2f5c"
            ink="white"
            className="md:col-span-4"
          />
        </div>
      </div>
    </section>
  );
}

function MarkCard({ mark, className = "", featured = false }) {
  return (
    <article
      data-testid={`mark-${mark.id}`}
      className={`group relative bg-[#0f0f0f] border border-neutral-900 hover:border-[#e11d2a]/40 transition-all overflow-hidden ${className}`}
    >
      {/* Subtle field lines */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            "repeating-linear-gradient(0deg, #fff 0 1px, transparent 1px 32px), repeating-linear-gradient(90deg, #fff 0 1px, transparent 1px 32px)",
        }}
      />

      {/* Logo display */}
      <div
        className={`relative flex items-center justify-center w-full h-full ${mark.pad}`}
      >
        <img
          src={mark.file}
          alt={mark.label}
          className={`max-w-full max-h-full object-contain transition-transform duration-500 group-hover:scale-[1.03] ${
            featured ? "w-[78%]" : "w-[62%]"
          }`}
          draggable={false}
        />
      </div>

      {/* Footer label */}
      <div className="absolute bottom-0 left-0 right-0 flex items-end justify-between gap-4 p-6 md:p-8 border-t border-neutral-900 bg-gradient-to-t from-black/60 to-transparent">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-1">
            {mark.note}
          </div>
          <div className="font-anton text-2xl md:text-3xl uppercase tracking-tight">
            {mark.label}
          </div>
        </div>
        <div className="hidden md:block text-right">
          <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 mb-1">
            Use it on
          </div>
          <div className="text-xs tracking-wide text-neutral-300">
            {mark.use}
          </div>
        </div>
      </div>
    </article>
  );
}

function ApparelMock({ title, tag, file, bg, ink, className = "" }) {
  const onLight = bg && bg.toLowerCase() === "#ffffff";
  return (
    <div
      data-testid={`apparel-${title.toLowerCase().replace(/\s+/g, "-")}`}
      className={`relative border border-neutral-900 overflow-hidden ${className}`}
      style={{ background: bg }}
    >
      <div className="aspect-[4/5] flex items-center justify-center p-12 relative">
        {/* Halftone */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              "radial-gradient(circle, #ffffff 1px, transparent 1px)",
            backgroundSize: "8px 8px",
            mixBlendMode: onLight ? "multiply" : "screen",
          }}
        />
        <img
          src={file}
          alt={title}
          className="relative max-w-[70%] max-h-[70%] object-contain drop-shadow-[0_8px_30px_rgba(0,0,0,0.4)]"
          draggable={false}
        />
      </div>
      <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
        <span className="text-[10px] tracking-[0.25em] uppercase font-bold text-white/80">
          {title}
        </span>
        <span className="text-[10px] tracking-[0.25em] uppercase text-white/60">
          {tag}
        </span>
      </div>
    </div>
  );
}
